import { FC, useMemo } from 'react';
import { BaseChartTemplate } from './BaseChartTemplate';
import type { FinancialHealthStats } from '../../types/financialStats';

// Constants for styling
const LINE_STYLES = {
  CURVED: 0.4
};

interface ActualCashOnHandChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<any>;
  monthsToShow?: number;
}

// Helper function to calculate trend line using linear regression
const calculateTrendLine = (data: number[]): number[] => {
  if (data.length <= 2) return data;
  
  // Use linear regression to calculate trend line
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  const n = data.length;
  
  // Calculate sums for linear regression formula
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += data[i];
    sumXY += i * data[i];
    sumXX += i * i;
  }
  
  // Calculate slope and y-intercept
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Generate trend line points
  return data.map((_, i) => intercept + slope * i);
};

// Determine cash health status
// @ts-ignore - Keeping for potential future use
const getCashHealthStatus = (cashAmount: number, avgAmount: number, monthsCovered: number) => {
  // Consider both the absolute amount and the months covered
  if (monthsCovered >= 6 || cashAmount > avgAmount * 1.5) {
    return {
      label: 'Excellent',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      icon: '💰'
    };
  } else if (monthsCovered >= 3 || cashAmount > avgAmount) {
    return {
      label: 'Good',
      color: 'text-sky-600',
      bgColor: 'bg-sky-50',
      icon: '👍'
    };
  } else if (monthsCovered >= 1) {
    return {
      label: 'Fair',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      icon: '⚠️'
    };
  } else {
    return {
      label: 'Low',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      icon: '⚡'
    };
  }
};

export const ActualCashOnHandChart: FC<ActualCashOnHandChartProps> = ({ 
  data, 
  showDebug, 
  onExpandClick,
  monthsToShow = 12
}) => {
  // Format currency for Y-axis
  const formatCurrency = (value: number) => {
    if (value === 0) return '$0';
    // Always display in $k units for consistency
    return `$${(value/1000).toFixed(1)}k`;
  };
  
  // Format tooltip values
  const formatTooltip = (value: number) => [`$${value.toLocaleString()}`, 'Actual Cash on Hand'];
  
  // Calculate maximum for Y-axis (20% above the max value, rounded to nearest $1000)
  const calculateYMax = useMemo(() => {
    if (data.length === 0) return 5000;
    
    const maxValue = Math.max(...data.map(item => item.liquid_reserves || 0));
    if (maxValue === 0) return 5000;
    
    // Add 20% buffer and round up to nearest $1000
    const withBuffer = maxValue * 1.2;
    return Math.ceil(withBuffer / 1000) * 1000;
  }, [data]);
  
  // Prepare chart datasets
  const getChartDatasets = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Sort data chronologically
    const sortedData = [...data].sort((a, b) => 
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    // Get the most recent months up to monthsToShow
    const visibleData = sortedData.slice(-monthsToShow);
    
    // Extract liquid_reserves values
    const values = visibleData.map(d => d.liquid_reserves || 0);
    
    // Calculate trend line
    const trendLine = calculateTrendLine(values);
    
    return [
      {
        // Main data line
        label: 'Actual Cash on Hand',
        data: values,
        fill: true,
        tension: LINE_STYLES.CURVED,
        borderWidth: 2.5,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointBorderColor: 'rgba(14, 165, 233, 1)', // sky-500
        pointBorderWidth: 2,
        borderColor: '#0EA5E9', // sky-500
        backgroundColor: 'rgba(14, 165, 233, 0.1)',
      },
      {
        // Trend line
        label: 'Trend',
        data: trendLine,
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        borderColor: 'rgba(107, 114, 128, 0.8)',
        backgroundColor: 'transparent',
      }
    ];
  }, [data, monthsToShow]);
  
  // Render the overlay with current data
  const renderOverlay = (visibleData: FinancialHealthStats[]) => {
    if (visibleData.length === 0) return null;
    
    // Calculate metrics for the overlay
    const currentCash = visibleData[visibleData.length - 1]?.liquid_reserves || 0;
    const previousCash = visibleData[visibleData.length - 2]?.liquid_reserves || 0;
    const monthlyChange = currentCash - previousCash;
    
    return (
      <div className="absolute left-16 top-2 bg-white bg-opacity-95 rounded-lg shadow-sm text-left w-56">
        <div className="grid gap-2.5 p-3">
          <div>
            <div className="text-xs text-slate-500 mb-1">Current Cash on Hand</div>
            <div className="text-xl font-bold text-slate-800">${currentCash.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
          </div>
          
          <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
            <div>
              <div className="text-xs text-slate-500 mb-0.5">Monthly Change</div>
              {monthlyChange === 0 ? (
                <div className="text-sm font-medium">$0.00</div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span 
                    className="text-lg" 
                    style={{ 
                      color: monthlyChange > 0 ? '#10B981' : '#EF4444'
                    }}
                  >
                    {monthlyChange > 0 ? '↑' : '↓'}
                  </span>
                  <span className="text-sm font-medium">${Math.abs(monthlyChange).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      <BaseChartTemplate
        data={data}
        showDebug={showDebug}
        monthsToShow={monthsToShow}
        onExpandClick={onExpandClick}
        title="Actual Cash on Hand"
        dataKey="liquid_reserves"
        color="#0EA5E9" // sky-500
        yAxisFormatter={formatCurrency}
        tooltipFormatter={formatTooltip}
        renderOverlay={renderOverlay}
        max={calculateYMax}
        customDatasets={getChartDatasets}
        yAxisStepSize={1000} // Ensure steps of $1k
        isCurrentFinances={true} // Enable drag functionality
      />
    </div>
  );
}; 